import React, { useState, useEffect } from 'react';
import { FileCog, Save, Image as ImageIcon, Upload, Trash2, Crown, Search } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { carsysApi } from '../api/carsysApi';
import PlaatsAutocomplete from '../components/PlaatsAutocomplete';
import PhoneInput from '../components/PhoneInput';
import { titleCaseName, titleCasePlace } from '../lib/casing';
import { toast } from 'sonner';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';
export default function Gegevens() {

  const { t } = useT();  const { gegevens, updateGegevens, adresLookup, refreshAll } = useData();
  const { user } = useAuth();
  const [override, setOverride] = useState(null);
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  const [cloudStatus, setCloudStatus] = useState({ plan: 'NONE', planLimits: { cloud: false } });

  useEffect(() => { carsysApi.getCloudStatus().then(setCloudStatus).catch(() => {}); }, []);

  const data = override !== null ? override : gegevens;
  const setData = (v) => setOverride(typeof v === 'function' ? v(data) : v);

  const proPlan = !!cloudStatus.planLimits?.cloud;
  const hasLogo = !!gegevens?.logoPath;

  const save = async () => {
    try { await updateGegevens(data); setOverride(null); toast.success(t('Garagegegevens opgeslagen')); }
    catch { toast.error(t('Opslaan mislukt')); }
  };
  const upd = (k, v) => setData({ ...data, [k]: v });

  const autofillFromPostcode = async () => {
    if (!data.postcode || !data.adres) { toast.error(t('Vul eerst postcode en huisnummer in (huisnummer in adresveld)')); return; }
    const hnr = (data.adres.match(/\d+/) || [''])[0];
    if (!hnr) { toast.error(t('Geen huisnummer in adres')); return; }
    try {
      const a = await adresLookup(data.postcode, hnr);
      setData({ ...data, adres: `${a.straat} ${hnr}`, plaats: a.plaats, postcode: a.postcode });
      toast.success(t('Adres ingevuld'));
    } catch (e) { toast.error(t('Adres lookup mislukt'), { description: e.response?.data?.detail || '' }); }
  };

  const autofillFromStreet = async () => {
    // Adres veld bevat bv "Bagijnhof 33"; gebruik plaats om te vinden
    if (!data.adres) { toast.error(t('Vul eerst straat + huisnummer in (bv. "Bagijnhof 33")')); return; }
    if (!data.plaats) { toast.error(t('Vul ook plaats in (autocomplete helpt)')); return; }
    const m = data.adres.match(/^(.+?)\s+(\d+\S*)\s*$/);
    if (!m) { toast.error(t('Kan straat + huisnummer niet uit "') + data.adres + '" halen'); return; }
    try {
      const a = await carsysApi.adresLookupByStraat(m[1].trim(), m[2].trim(), data.plaats);
      setData({ ...data, adres: `${a.straat} ${a.huisnummer}`, plaats: a.plaats, postcode: a.postcode });
      toast.success(`Adres gevonden: ${a.weergavenaam}`);
    } catch (e) { toast.error(t('Adres niet gevonden'), { description: e.response?.data?.detail || '' }); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('Logo te groot (max 2 MB)')); return; }
    try {
      await carsysApi.uploadLogo(file);
      toast.success(t('Logo geüpload'));
      await refreshAll();
      setLogoCacheBust(Date.now());
    } catch (err) {
      toast.error(t('Upload mislukt'), { description: err.response?.data?.detail || err.message });
    } finally {
      e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!(await confirmDialog(t('Logo verwijderen?')))) return;
    try {
      await carsysApi.deleteLogo();
      toast.success(t('Logo verwijderd'));
      await refreshAll();
      setLogoCacheBust(Date.now());
    } catch (err) {
      toast.error(t('Verwijderen mislukt'), { description: err.response?.data?.detail || err.message });
    }
  };

  const logoSrc = hasLogo
    ? `${carsysApi.logoUrl(user.garageId)}?t=${logoCacheBust || 1}&tok=${localStorage.getItem('monteurcentraal_token') || ''}`
    : null;

  return (
    <div data-testid="gegevens-page">
      <div className="cs-page-header">
        <div><h2>{t('page.garage.title')}</h2><p>{t('page.garage.sub')}</p></div>
        <button data-testid="btn-lookup-postcode" className="cs-btn" title={t('Vul plaats en straat in via postcode + huisnr')} onClick={autofillFromPostcode}><Search size={14} /> {t('Via postcode')}</button>
        <button data-testid="btn-lookup-street" className="cs-btn" title={t('Vul postcode in via straat + huisnr + plaats')} onClick={autofillFromStreet}><Search size={14} /> {t('Via straat')}</button>
        <button data-testid="btn-save-gegevens" className="cs-btn primary" onClick={save}><Save size={14} /> {t('Opslaan')}</button>
      </div>

      <div className="cs-card padded" style={{ maxWidth: 720, marginBottom: 14 }} data-testid="card-logo">
        <h3 className="cs-card-title"><ImageIcon size={16} /> {t(' Bedrijfslogo op factuur')}
          {!proPlan && <span className="cs-badge blue" style={{ marginLeft: 8, fontSize: 10 }}><Crown size={10} /> PRO/Enterprise</span>}
        </h3>
        {!proPlan ? (
          <div style={{ padding: 14, background: 'var(--cs-amber-soft)', borderRadius: 8, color: 'var(--cs-amber)', fontSize: 13 }}>
            {t('Logo op de factuur is beschikbaar in <b>PRO</b> en <b>ENTERPRISE</b>. Activeer een licentie via <b>Instellingen → Plannen</b> om je eigen logo bovenaan offertes en facturen te tonen.')}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <div style={{ width: 140, height: 90, border: '1px dashed var(--cs-border)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafbfc', overflow: 'hidden' }}>
              {hasLogo
                ? <LogoImage src={logoSrc} />
                : <span style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>{t('Geen logo', 'Geen logo')}</span>}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ marginTop: 0, fontSize: 12.5, color: 'var(--cs-text-muted)' }}>{t('PNG of JPG, max 2 MB. Wordt links bovenaan getoond op je bonnen (vervangt de tekstlogo).')}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <label className="cs-btn primary" style={{ cursor: 'pointer' }}>
                  <Upload size={14} /> {hasLogo ? t('Vervangen') : t('Upload logo')}
                  <input data-testid="input-logo-file" type="file" accept="image/png,image/jpeg" onChange={handleLogoUpload} style={{ display: 'none' }} />
                </label>
                {hasLogo && (
                  <button data-testid="btn-delete-logo" className="cs-btn danger" onClick={handleLogoDelete}><Trash2 size={14} /> {t('Verwijderen')}</button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="cs-card padded" style={{ maxWidth: 720 }}>
        <h3 className="cs-card-title"><FileCog size={16} /> {t('Bedrijfsinformatie')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Bedrijfsnaam')}</label><input className="cs-input" value={data.naam || ''} onChange={e => upd('naam', e.target.value)} onBlur={e => upd('naam', titleCaseName(e.target.value))} /></div>
          <div style={{ gridColumn: '1 / 3' }}>
            <label className="cs-section-title">{t('Adres')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>{t('(straat + huisnr — klik 🔍 om plaats/postcode op te zoeken)')}</span></label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="cs-input" value={data.adres || ''} onChange={e => upd('adres', e.target.value)} placeholder={t('Bagijnhof 33')} />
              <button type="button" className="cs-btn" onClick={autofillFromStreet} title={t('Adres opzoeken via PDOK (straat + huisnr + plaats)')}><Search size={14} /></button>
            </div>
          </div>
          <div>
            <label className="cs-section-title">{t('Postcode')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>{t('(🔍 vult straat/plaats automatisch)')}</span></label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="cs-input" value={data.postcode || ''} onChange={e => upd('postcode', e.target.value.toUpperCase())} placeholder="1671 BR" />
              <button type="button" className="cs-btn" onClick={autofillFromPostcode} title={t('Adres opzoeken via PDOK (postcode + huisnr)')}><Search size={14} /></button>
            </div>
          </div>
          <div><label className="cs-section-title">{t('Plaats')}</label>
            <PlaatsAutocomplete value={data.plaats || ''} onChange={(v) => upd('plaats', v)} placeholder={t('bv. Medemblik')} testid="plaats-input" />
          </div>
          <div><label className="cs-section-title">{t('Telefoon')}</label><PhoneInput value={data.telefoon || ''} onChange={(v) => upd('telefoon', v)} testid="garage-telefoon" /></div>
          <div><label className="cs-section-title">{t('E-mail')}</label><input className="cs-input" value={data.email || ''} onChange={e => upd('email', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('KvK nummer')}</label><input className="cs-input" value={data.kvk || ''} onChange={e => upd('kvk', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('BTW nummer')}</label><input className="cs-input" value={data.btw || ''} onChange={e => upd('btw', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('IBAN')}</label><input className="cs-input" value={data.iban || ''} onChange={e => upd('iban', e.target.value)} /></div>
        </div>
      </div>

      <div className="cs-card padded" style={{ maxWidth: 720, marginTop: 14 }} data-testid="card-standaardcodes">
        <h3 className="cs-card-title"><FileCog size={16} /> {t('Standaard artikelcodes', 'Standaard artikelcodes')}</h3>
        <p style={{ marginTop: 0, fontSize: 12.5, color: 'var(--cs-text-muted)' }}>
          {t('Bedragen die met één klik als regel op een bon kunnen worden gezet.', 'Bedragen die met één klik als regel op een bon kunnen worden gezet.')}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="cs-section-title">{t('Milieutoeslag', 'Milieutoeslag')} (€)</label>
            <input type="number" step="0.01" min="0" className="cs-input" data-testid="input-milieutoeslag" value={data.milieutoeslagBedrag ?? 7.5} onChange={e => upd('milieutoeslagBedrag', e.target.value)} placeholder="7,50" />
          </div>
          <div>
            <label className="cs-section-title">{t('Kleinmateriaal', 'Kleinmateriaal')} (% {t('over arbeid', 'over arbeid')})</label>
            <input type="number" step="0.1" min="0" className="cs-input" data-testid="input-kleinmateriaal" value={data.kleinmateriaalPercentage ?? 5} onChange={e => upd('kleinmateriaalPercentage', e.target.value)} placeholder="5" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Logo image - fetches with auth header and shows as blob URL
function LogoImage({ src }) {
  const { t } = useT();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tok = localStorage.getItem('monteurcentraal_token');
    fetch(src.split('?')[0], { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(b => { if (!cancelled) setUrl(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  if (!url) return <span style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>{t('Laden...')}</span>;
  return <img src={url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} data-testid="logo-preview" />;
}
